/**
 * firebasePush.ts - FCM 웹 푸시 초기화/토큰 관리
 *
 * - .env의 REACT_APP_FIREBASE_* 값이 없으면 조용히 비활성 (로컬/미설정 환경 영향 없음)
 * - 로그인 후 initFcm(): 알림 권한 요청 -> SW 등록 -> 토큰 발급 -> 백엔드 등록
 * - 로그아웃 시 removeFcmToken(): 백엔드에서 토큰 삭제 (공용 PC 대응)
 * - 수신 시 pushStore.refresh()로 백엔드 알림 목록을 다시 불러 헤더 종 아이콘에 빨간 점을 표시
 *   (포그라운드는 onMessage, 백그라운드는 public/firebase-messaging-sw.js -> postMessage)
 */
import { initializeApp, getApps, getApp } from "firebase/app";
import {
  getMessaging,
  getToken,
  onMessage,
  isSupported,
} from "firebase/messaging";
import { apiFetch } from "@shared/api/api";
import { usePushStore } from "@shared/libs/pushStore";

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY ?? "",
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN ?? "",
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID ?? "",
  messagingSenderId: process.env.REACT_APP_FIREBASE_SENDER_ID ?? "",
  appId: process.env.REACT_APP_FIREBASE_APP_ID ?? "",
};

const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY ?? "";
const TOKEN_STORAGE_KEY = "fcm_dc_token";

let initialized = false;
let swListenerAttached = false;

/**
 * 수신 알림 처리: payload 내용은 쓰지 않고 "새 알림 도착" 신호로만 사용해
 * 백엔드 /alarm/list(getRecentFcmAlarm)를 다시 불러 목록·미확인 수를 서버 기준으로 맞춘다.
 */
function pushToStore(_payload: any) {
  void usePushStore.getState().refresh();
}

/**
 * 백그라운드(다른 탭/최소화) 수신은 firebase-messaging-sw.js가 처리하는데,
 * 그 SW가 열려있는 탭들에 postMessage({type:"FCM_BACKGROUND_MESSAGE"})를 보낸다.
 * 이를 받아 종 아이콘 상태를 갱신한다.
 */
function attachSwMessageListener() {
  if (swListenerAttached) return;
  swListenerAttached = true;
  navigator.serviceWorker.addEventListener("message", (event) => {
    if (event.data?.type !== "FCM_BACKGROUND_MESSAGE") return;
    pushToStore(event.data.payload);
  });
}

function isConfigured(): boolean {
  return Boolean(
    firebaseConfig.apiKey &&
      firebaseConfig.projectId &&
      firebaseConfig.messagingSenderId &&
      firebaseConfig.appId &&
      VAPID_KEY
  );
}

/**
 * FCM 초기화 + 토큰 발급 + 백엔드 등록.
 * 로그인 상태에서 호출할 것 (register API가 세션 인증 필요).
 * 실패해도 throw하지 않고 로그만 남긴다 - 푸시는 부가기능이므로 업무 화면을 막지 않는다.
 */
export async function initFcm(): Promise<void> {
  try {
    if (initialized) return;
    if (!isConfigured()) {
      console.debug("[FCM] REACT_APP_FIREBASE_* 미설정 - 푸시 비활성");
      return;
    }
    if (!("serviceWorker" in navigator) || !("Notification" in window)) return;
    if (!(await isSupported().catch(() => false))) return;

    const permission = await Notification.requestPermission();
    if (permission !== "granted") {
      console.debug("[FCM] 알림 권한 거부됨");
      return;
    }

    // public/firebase-messaging-sw.js는 웹팩 밖이라 env를 못 읽는다.
    // config를 쿼리스트링으로 전달해 SW 쪽에서 초기화한다.
    const params = new URLSearchParams({
      apiKey: firebaseConfig.apiKey,
      authDomain: firebaseConfig.authDomain,
      projectId: firebaseConfig.projectId,
      messagingSenderId: firebaseConfig.messagingSenderId,
      appId: firebaseConfig.appId,
    });
    const registration = await navigator.serviceWorker.register(
      `/firebase-messaging-sw.js?${params.toString()}`
    );

    const app = getApps().length ? getApp() : initializeApp(firebaseConfig);
    const messaging = getMessaging(app);

    const token = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: registration,
    });
    if (!token) {
      console.debug("[FCM] 토큰 발급 실패");
      return;
    }

    await apiFetch("/push/token/register", {
      method: "POST",
      body: { dc_token: token, tp_device: "WEB" } as any,
    });
    try {
      localStorage.setItem(TOKEN_STORAGE_KEY, token);
    } catch {
      /* storage 차단 환경 무시 */
    }

    // 포그라운드(화면 보고 있는 중) 수신: 종 아이콘 빨간 점만 켠다
    onMessage(messaging, (payload) => {
      pushToStore(payload);
    });
    attachSwMessageListener();

    initialized = true;
    console.debug("[FCM] 초기화 완료");
  } catch (e) {
    console.debug("[FCM] 초기화 실패", e);
  }
}

/**
 * 로그아웃 시 호출. 백엔드 sys_user_device에서 이 브라우저 토큰을 삭제해
 * 로그아웃한 사용자에게 알림이 가지 않게 한다.
 */
export async function removeFcmToken(): Promise<void> {
  let token: string | null = null;
  try {
    token = localStorage.getItem(TOKEN_STORAGE_KEY);
    localStorage.removeItem(TOKEN_STORAGE_KEY);
  } catch {
    /* ignore */
  }
  if (!token) return;

  await apiFetch("/push/token/remove", {
    method: "POST",
    body: { dc_token: token } as any,
  }).catch(() => {
    /* 로그아웃 시점 실패는 무시 - 만료 토큰은 발송 응답에서 정리됨 */
  });
}
